// <EventTime> — render a fixed instant in the site / auction zone, with the
// viewer's alongside when it differs (BL-596 / ADR 024 § 3, as amended by
// BUG-1581). Single-zone by default; dual-zone when a viewer
// zone is supplied and differs from the site zone. All formatting + zone
// resolution comes from the shared @auctogon/time service, so admin (single
// zone) and bidder (dual zone) render through the same component.

import type { ReactElement } from 'react';
import {
  formatEventTime,
  resolveDisplayZone,
  type DisplayContext,
  type FormatEventTimeOptions,
  type TimeInput,
} from '@auctogon/time';
import { toIso } from './time-utils';

export interface EventTimeProps {
  /** The fixed instant (ISO string, epoch ms, or Date). */
  instant: TimeInput;
  /** Auction-house / site IANA zone. Always the fallback. */
  siteZone: string;
  /** Viewer's IANA zone, when known. Drives dual-zone display. */
  viewerZone?: string | null;
  /**
   * The kind of time being shown. The site / auction zone leads in both cases —
   * what differs is whether a second zone appears at all.
   * - `attending` (default): site zone leads, viewer's alongside when it differs.
   * - `record`: a single frozen zone-of-record, never dual.
   *
   * A `planning` option that led with the VIEWER's zone was retired by BUG-1581
   * (ADR 024 § 3) after shipping with zero callers.
   */
  context?: DisplayContext;
  /** Format options forwarded to `formatEventTime` (omitYear, timeOnly, …). */
  format?: FormatEventTimeOptions;
  className?: string;
}

/**
 * Render a fixed instant. When `viewerZone` differs from `siteZone` (and the
 * context isn't `record`), shows both zones — leading zone first, the other in
 * parentheses (e.g. "9:00 AM PT (11:00 AM CT)"). Otherwise a single labeled
 * time. Wraps the value in a semantic `<time>` element with a machine-readable
 * `dateTime`.
 */
export function EventTime({
  instant,
  siteZone,
  viewerZone,
  context = 'attending',
  format,
  className,
}: EventTimeProps): ReactElement {
  const zones = resolveDisplayZone({ context, siteZone, viewerZone });
  const primary = formatEventTime(instant, zones.primary, format);

  const body = zones.showDual
    ? `${primary} (${formatEventTime(instant, zones.secondary as string, format)})`
    : primary;

  const iso = toIso(instant);
  return iso ? (
    <time dateTime={iso} className={className}>
      {body}
    </time>
  ) : (
    <span className={className}>{body}</span>
  );
}
